import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const B = '#0B3D6E', T = '#0F6E56';

const STATUS_STYLES = { pending:'#fef3c7|#d97706', confirmed:'#E6F1FB|#0B3D6E', delivered:'#E1F5EE|#085041', cancelled:'#fee2e2|#dc2626' };

function StatusBadge({ status }) {
  const labels = { pending:'En attente', confirmed:'Confirmée', delivered:'Livrée', cancelled:'Annulée' };
  const [bg, color] = (STATUS_STYLES[status]||'#f1f5f9|#64748b').split('|');
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:600, background:bg, color }}><span style={{ width:6, height:6, borderRadius:'50%', background:color }}/>{labels[status]||status}</span>;
}

function KPI({ label, value, icon, bg, color, to, sub }) {
  const inner = (
    <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.2rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', height:'100%', transition:'border-color 0.15s' }}
      onMouseEnter={e=>{ if(to) e.currentTarget.style.borderColor=B; }}
      onMouseLeave={e=>{ if(to) e.currentTarget.style.borderColor='#e2e8f0'; }}>
      <div style={{ width:38, height:38, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, marginBottom:10 }}>{icon}</div>
      <div style={{ fontSize:24, fontWeight:600, color:color||'#1a2535', marginBottom:2 }}>{value}</div>
      <div style={{ fontSize:12, color:'#64748b' }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{sub}</div>}
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration:'none' }}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get('/stats').then(r => r.data.data),
    refetchInterval: 30000
  });

  if (isLoading) return <div style={{ padding:'2rem', textAlign:'center', color:'#64748b' }}>Chargement...</div>;

  const months = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
  const barData = data?.ordersByMonth || [];
  const maxVal  = Math.max(...barData.map(m=>m.count), 1);

  const statusData = [
    { label:'Livrées',    count:data?.orders?.delivered||0, color:T },
    { label:'Confirmées', count:data?.orders?.confirmed||0, color:B },
    { label:'En attente', count:data?.orders?.pending||0,   color:'#d97706' },
    { label:'Annulées',   count:data?.orders?.cancelled||0, color:'#dc2626' },
  ];
  const total = data?.orders?.total || 1;

  return (
    <div style={{ padding:'1.5rem', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
        <h1 style={{ fontSize:20, fontWeight:600 }}>Tableau de bord</h1>
        <div style={{ fontSize:12, color:'#94a3b8' }}>Mis à jour toutes les 30s · {new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</div>
      </div>

      {/* KPIs Row 1 — LOMED Orders */}
      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#64748b', marginBottom:10 }}>Commandes LOMED MEDICARE</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:'1.5rem' }}>
        <KPI label="Total commandes" value={data?.orders?.total||0}     icon="📋" bg="#E6F1FB" color={B} to="/admin/orders"/>
        <KPI label="En attente"      value={data?.orders?.pending||0}   icon="⏳" bg="#fef3c7" color="#d97706" to="/admin/orders"/>
        <KPI label="Confirmées"      value={data?.orders?.confirmed||0} icon="✅" bg="#E1F5EE" color={T} to="/admin/orders"/>
        <KPI label="Livrées"         value={data?.orders?.delivered||0} icon="🚚" bg="#E1F5EE" color={T} to="/admin/orders"/>
        <KPI label="Catalogue"       value={data?.products?.total||0}   icon="💊" bg="#f3f0ff" color="#7C3AED" to="/admin/products"/>
      </div>

      {/* KPIs Row 2 — StockMed + Contacts */}
      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.8px', color:'#64748b', marginBottom:10 }}>StockMed & Communications</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:'1.5rem' }}>
        <KPI label="Clients StockMed" value={data?.clients?.total||0}  icon="👥" bg="#E6F1FB" color={B} to="/admin/users" sub={`${data?.clients?.free||0} gratuit · ${data?.clients?.pro||0} pro`}/>
        <KPI label="Stock total"      value={data?.stock?.total||0}    icon="📦" bg="#E1F5EE" color={T} to="/admin/stock"/>
        <KPI label="Messages contact" value={data?.contacts?.unread||0} icon="✉️" bg={data?.contacts?.unread>0?"#fee2e2":"#f1f5f9"} color={data?.contacts?.unread>0?"#dc2626":"#64748b"} to="/admin/contacts" sub={data?.contacts?.unread>0?"Non lus":"Aucun nouveau"}/>
      </div>

      {/* Charts */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:'1.5rem' }}>
        {/* Bar chart */}
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <h3 style={{ fontSize:14, fontWeight:600 }}>Commandes par mois</h3>
            <Link to="/admin/orders" style={{ fontSize:11, color:B, textDecoration:'none' }}>Voir tout →</Link>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:110 }}>
            {barData.length ? barData.map((m,i) => (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                <span style={{ fontSize:10, fontWeight:600, color:B }}>{m.count}</span>
                <div style={{ width:'100%', background:`linear-gradient(180deg,${B},${T})`, borderRadius:'4px 4px 0 0', minHeight:4, height:Math.round((m.count/maxVal)*90) }}/>
                <span style={{ fontSize:9, color:'#64748b' }}>{months[(m._id?.month||1)-1]}</span>
              </div>
            )) : <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8', fontSize:13 }}>Aucune donnée</div>}
          </div>
        </div>

        {/* Status donut-like */}
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <h3 style={{ fontSize:14, fontWeight:600 }}>Statuts des commandes</h3>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {statusData.map(s => (
              <div key={s.label} style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:s.color, flexShrink:0 }}/>
                <div style={{ flex:1, fontSize:12, color:'#475569' }}>{s.label}</div>
                <div style={{ fontSize:13, fontWeight:700, minWidth:24 }}>{s.count}</div>
                <div style={{ width:80, background:'#f1f5f9', borderRadius:4, height:8, overflow:'hidden' }}>
                  <div style={{ height:8, background:s.color, width:`${Math.round((s.count/total)*100)}%`, borderRadius:4, transition:'width 0.5s' }}/>
                </div>
                <div style={{ fontSize:11, color:'#94a3b8', minWidth:30 }}>{Math.round((s.count/total)*100)}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Clients StockMed summary */}
      {(data?.clients?.total > 0) && (
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1.2rem 1.5rem', marginBottom:'1.5rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:42, height:42, background:'#E6F1FB', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>📦</div>
            <div>
              <div style={{ fontSize:14, fontWeight:600 }}>StockMed — {data?.clients?.total} client(s) actif(s)</div>
              <div style={{ fontSize:12, color:'#64748b' }}>{data?.clients?.free||0} plan gratuit · {data?.clients?.pro||0} plan Pro · {data?.stock?.total||0} produits en stock total</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Link to="/admin/stock" style={{ background:B, color:'#fff', padding:'7px 14px', borderRadius:8, fontSize:12, fontWeight:600, textDecoration:'none' }}>Voir le stock →</Link>
            <Link to="/admin/users" style={{ background:'#f1f5f9', color:'#64748b', padding:'7px 14px', borderRadius:8, fontSize:12, textDecoration:'none' }}>Gérer les clients →</Link>
          </div>
        </div>
      )}

      {/* Recent orders */}
      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.06)', overflow:'hidden' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.2rem', borderBottom:'1px solid #f1f5f9' }}>
          <h3 style={{ fontSize:14, fontWeight:600 }}>Dernières commandes LOMED</h3>
          <Link to="/admin/orders" style={{ fontSize:12, color:B, textDecoration:'none' }}>Voir toutes →</Link>
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['N°','Établissement','Ville','Date','Statut'].map(h=>(
                  <th key={h} style={{ textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', padding:'10px 14px', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.recentOrders||[]).map(o=>(
                <tr key={o._id} style={{ borderTop:'1px solid #f1f5f9' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11, color:'#94a3b8' }}>{o.orderNumber}</td>
                  <td style={{ padding:'10px 14px', fontSize:13, fontWeight:500 }}>{o.institution}</td>
                  <td style={{ padding:'10px 14px', fontSize:13, color:'#64748b' }}>{o.city}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{new Date(o.createdAt).toLocaleDateString('fr-FR')}</td>
                  <td style={{ padding:'10px 14px' }}><StatusBadge status={o.status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.recentOrders?.length && <div style={{ textAlign:'center', padding:'2rem', color:'#94a3b8' }}>Aucune commande</div>}
        </div>
      </div>
    </div>
  );
}

